// API utilities for student authentication
// Production uses levav.co domain (valid SSL), local dev uses IP
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://levav.co/custom-api/v1'
  : 'http://18.166.174.10/custom-api/v1';

// Store session token in localStorage
export const saveSession = (sessionToken, userId, userName = null) => {
  localStorage.setItem('sessionToken', sessionToken);
  localStorage.setItem('userId', userId);
  if (userName) {
    localStorage.setItem('userName', userName);
  }
};

// Get session token from localStorage
export const getSessionToken = () => {
  return localStorage.getItem('sessionToken');
};

// Get user ID from localStorage
export const getUserId = () => {
  return localStorage.getItem('userId');
};

// Get user name from localStorage
export const getUserName = () => {
  return localStorage.getItem('userName');
};

// Clear session data
export const clearSession = () => {
  localStorage.removeItem('sessionToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userName');
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getSessionToken();
};

// Student login API
export const studentLogin = async (email, password, platform = 'web') => {
  try {
    // Clear any existing session data before login
    clearSession();
    
    console.log('=== Calling Login API ===');
    console.log('Email:', email);
    console.log('API URL:', `${API_BASE_URL}/student/login`);
    
    const response = await fetch(`${API_BASE_URL}/student/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        login_email: email,
        login_password: password,
        platform: platform,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      console.error('Login API Error:', error);
      throw new Error(error.error || 'Login failed');
    }

    const data = await response.json();
    console.log('Login API Success:', data);
    return data; // { session_token, user_id }
  } catch (error) {
    console.error('Login Exception:', error);
    throw error;
  }
};

// Student logout API
export const studentLogout = async () => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return;
  }

  try {
    await fetch(`${API_BASE_URL}/student/logout`, {
      method: 'POST',
      headers: {
        'Session-Token': sessionToken,
      },
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    clearSession();
  }
};

// Verify session token
export const verifySession = async () => {
  const sessionToken = getSessionToken();
  if (!sessionToken) {
    return false;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/student/verify`, {
      method: 'GET',
      headers: {
        'Session-Token': sessionToken,
      },
    });

    if (!response.ok) {
      clearSession();
      return false;
    }

    const data = await response.json();
    
    console.log('=== Verify Session Response ===');
    console.log('Valid:', data.valid);
    console.log('UserId from API:', data.userId);
    console.log('Name from API:', data.name);
    console.log('Email from API:', data.email);
    console.log('==============================');
    
    // Save user info if available (name is now included in verify response)
    // IMPORTANT: Update userId and userName from verify response to ensure consistency
    if (data.valid && data.userId) {
      // Update userId in localStorage (in case it changed or was outdated)
      const oldUserId = localStorage.getItem('userId');
      if (oldUserId && oldUserId !== data.userId.toString()) {
        console.warn(`UserId mismatch! Old: ${oldUserId}, New: ${data.userId}`);
      }
      localStorage.setItem('userId', data.userId.toString());
      
      // Update userName if available
      if (data.name) {
        localStorage.setItem('userName', data.name);
      }
    }
    
    return data.valid;
  } catch (error) {
    clearSession();
    return false;
  }
};

// Authenticated fetch wrapper
export const authenticatedFetch = async (url, options = {}) => {
  const sessionToken = getSessionToken();
  
  if (!sessionToken) {
    throw new Error('No session token found');
  }

  const headers = {
    ...options.headers,
    'Session-Token': sessionToken,
  };

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    clearSession();
    window.location.href = '/student-login';
    throw new Error('Session expired');
  }

  return response;
};

// Get assigned assessments
export const getAssignedAssessments = async () => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/student/assessments/assigned`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch assigned assessments');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching assigned assessments:', error);
    throw error;
  }
};

// Get global assessments
export const getGlobalAssessments = async () => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/student/assessments/global`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch global assessments');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching global assessments:', error);
    throw error;
  }
};

// Get assessment detail
export const getAssessmentDetail = async (assessmentId, taskId = null) => {
  try {
    let url = `${API_BASE_URL}/student/assessments/${assessmentId}`;
    if (taskId) {
      url += `?taskId=${taskId}`;
    }
    
    const response = await authenticatedFetch(url);
    
    if (!response.ok) {
      throw new Error('Failed to fetch assessment detail');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching assessment detail:', error);
    throw error;
  }
};

// Get assessment questions
export const getAssessmentQuestions = async (assessmentId) => {
  try {
    const response = await authenticatedFetch(`${API_BASE_URL}/student/assessments/${assessmentId}/questions`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch assessment questions');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching assessment questions:', error);
    throw error;
  }
};

// Submit assessment answers
export const submitAssessment = async (assessmentId, answers, taskId = null, totalTime = null, questionTimes = null) => {
  try {
    const userId = getUserId();
    if (!userId) {
      throw new Error('User not logged in');
    }

    // Format answers according to backend API structure
    const formattedAnswers = answers.map(answer => ({
      questionId: answer.questionId,
      optionIds: [answer.optionId], // Backend expects array of option IDs
      timeSpent: answer.timeSpent, // Include time spent per question
    }));

    const requestBody = {
      userId: parseInt(userId),
      assessmentId: parseInt(assessmentId),
      taskId: taskId ? parseInt(taskId) : null,
      answers: formattedAnswers,
      totalTime: totalTime, // Add total time for entire assessment
    };

    console.log('=== Submitting Assessment ===');
    console.log('Request body:', requestBody);
    console.log('Answers count:', formattedAnswers.length);
    console.log('Total time:', totalTime, 'seconds');
    console.log('============================');

    const response = await authenticatedFetch(`${API_BASE_URL}/assessment/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Submit failed:', errorData);
      throw new Error(errorData.error || 'Failed to submit assessment');
    }

    const result = await response.json();
    console.log('=== Submit Success ===');
    console.log('Result:', result);
    console.log('=====================');
    
    return result;
  } catch (error) {
    console.error('Error submitting assessment:', error);
    throw error;
  }
};
